import { FlexBox, Icon, Text, Tooltip } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { type MouseEvent, useCallback } from 'react';

import { appActions } from 'app/app.actions';
import { rewindConfig } from 'components/rewind';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useServerStorage } from 'utils/useServerStorage/useServerStorage';
import { useCurrentUser } from 'utils/user';

import styles from './rewindButton.module.scss';

type RewindButtonProps = {
  /**
   * If true, a dismiss button will be shown. If false, the button is always
   * visible regardless of the dismissed state.
   */
  isDismissable?: boolean;
  /** The location where the button is displayed, used for tracking. */
  location: 'sidebar' | 'analytics';
  /** Callback function to be called when the button is clicked. */
  onClick?: () => void;
} & PropsWithClassName;

export const RewindButton = ({
  className,
  isDismissable = true,
  location,
  onClick,
}: RewindButtonProps): JSX.Element | null => {
  const { user } = useCurrentUser();
  const dispatch = useAppDispatch();
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const flags = useFeatureFlag(['dixa-rewind']);
  const storage = useServerStorage('user');
  const isDismissed = storage.data.global?.rewind?.[rewindConfig.year] === true;

  const handleClick = useCallback(() => {
    ampli.rewindOpened({ year: rewindConfig.year, location });

    if (onClick) {
      onClick();
    } else {
      dispatch(
        appActions.showModal({
          modal: {
            type: 'app:rewind',
            arg: { userId: user?.id },
          },
        }),
      );
    }
  }, [dispatch, location, onClick, user?.id]);

  const handleDismiss = useCallback(
    async (event: MouseEvent) => {
      event.stopPropagation();

      const { isConfirmed } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'app:confirm',
            arg: {
              title: 'Dismiss Dixa Rewind?',
              description: `You can still find your Dixa Rewind ${rewindConfig.year} in Intelligence.`,
              confirm: {
                label: 'Dismiss',
                variant: 'primary',
              },
              cancel: {
                label: 'Cancel',
              },
            },
          },
        }),
      );

      if (isConfirmed) {
        ampli.rewindDismissed({ year: rewindConfig.year });

        storage.set({
          global: (draft) => {
            if (!draft.rewind) {
              draft.rewind = {};
            }
            draft.rewind[rewindConfig.year] = true;
          },
        });
      }
    },
    [dispatch, storage],
  );

  if (isSmallScreen || !flags.dixaRewind || (isDismissed && isDismissable)) {
    return null;
  }

  const tooltipContent = (
    <FlexBox gap="200" flexDirection="column">
      <FlexBox alignItems="center" gap="200">
        <Icon name="volume-up" size={16} />

        <Text.Paragraph variant="2" as="span" color="inherit">
          This feature includes background music
        </Text.Paragraph>
      </FlexBox>

      <FlexBox alignItems="center" gap="200">
        <Icon name="working" size={16} />

        <Text.Paragraph variant="2" as="span" color="inherit">
          For best experience, set yourself to Away
        </Text.Paragraph>
      </FlexBox>
    </FlexBox>
  );

  return (
    <Tooltip
      content={tooltipContent}
      side="right"
      wrap="div"
      wrapProps={{
        className,
      }}
    >
      <button
        className={styles.rewindButton}
        onClick={handleClick}
        type="button"
      >
        <div className={styles.rewindButtonInner}>
          <span className={styles.icon}>🎁</span>

          <Text.Paragraph className={styles.text} variant="1" as="span">
            Your <span className={styles.year}>{rewindConfig.year}</span> Dixa
            Rewind
          </Text.Paragraph>
        </div>

        {isDismissable && (
          <div
            className={styles.dismissButton}
            onClick={handleDismiss}
            role="button"
            tabIndex={0}
          >
            <Icon name="close" />
          </div>
        )}
      </button>
    </Tooltip>
  );
};
